import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import { useRequest } from "../hooks/useRequest";
import { getPaymentMethods } from "../api/paymentMethods";
import { upgradeSubscription, Plan } from "../api/subscription";
import { getStripe } from "../lib/stripe";
import { formatCents, formatNumber } from "../utils/format";
import "./planSwitch.scss";

const feeLabel = (cents: number) =>
  cents <= 0
    ? "$0"
    : `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const brand = (b: string) => b.charAt(0).toUpperCase() + b.slice(1);

const UpgradeForm: React.FC<{
  currentPlan?: Plan;
  targetPlan: Plan;
  onSuccess: () => void;
  onClose: () => void;
}> = ({ currentPlan, targetPlan, onSuccess, onClose }) => {
  const navigate = useNavigate();
  const cards = useRequest(getPaymentMethods);

  // 跳转到账户设置添加银行卡（先关闭弹窗）
  const goAddCard = () => {
    onClose();
    navigate("/account-settings");
  };

  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!cards.data || selectedCardId) return;
    const def = cards.data.find((c) => c.isDefault) || cards.data[0];
    if (def) setSelectedCardId(def.id);
  }, [cards.data, selectedCardId]);

  const totalLabel = feeLabel(targetPlan.monthlyFeeCents);

  const handleUpgrade = async () => {
    setError(null);
    if (!selectedCardId) {
      setError("Please select a payment method.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await upgradeSubscription(selectedCardId);
      if (res.status === "requires_action" && res.clientSecret) {
        const stripe = await getStripe();
        if (!stripe) return setError("Payment is not configured.");
        const { error: sErr, paymentIntent } = await stripe.confirmCardPayment(
          res.clientSecret
        );
        if (sErr) return setError(sErr.message || "Payment failed.");
        if (paymentIntent?.status !== "succeeded")
          return setError("Payment could not be completed. Please try again.");
      } else if (res.status === "requires_payment_method") {
        return setError(
          res.message || "Card declined. Please try a different card."
        );
      }
      // 成功（含 3DS 通过）
      setSucceeded(true);
      // webhook 可能有延迟，稍后再刷新订阅信息
      setTimeout(onSuccess, 1500);
    } catch (e) {
      setError(
        e instanceof Error
          ? `${e.message} You can safely retry.`
          : "Request failed. You can safely retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-plan-switch">
      <div className="cs-plan-switch__header">
        <h2 className="cs-plan-switch__title">
          Upgrade to {targetPlan.planName}
        </h2>
        <button
          type="button"
          className="cs-plan-switch__close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <p className="cs-plan-switch__subtitle">
        You are upgrading from {currentPlan?.planName || "current plan"} (
        {feeLabel(currentPlan?.monthlyFeeCents ?? 0)}/mo) to{" "}
        {targetPlan.planName} ({totalLabel}/mo).
      </p>

      <div className="cs-plan-switch__box cs-plan-switch__box--upgrade">
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Effective: </span>
          Immediately after payment
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Monthly Credits: </span>
          {formatCents(currentPlan?.monthlyCreditsCents ?? 0)}
          <span className="cs-plan-switch__arrow">→</span>
          {formatCents(targetPlan.monthlyCreditsCents)}
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Rate Limit: </span>
          {formatNumber(currentPlan?.rateLimitQps ?? 0)}
          <span className="cs-plan-switch__arrow">→</span>
          {formatNumber(targetPlan.rateLimitQps)} req/s
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Max API Keys: </span>
          {currentPlan?.maxApiKeys ?? 0}
          <span className="cs-plan-switch__arrow">→</span>
          {targetPlan.maxApiKeys}
        </div>
      </div>

      <h3 className="cs-plan-switch__section-title">Payment</h3>
      {cards.loading ? (
        <div className="cs-plan-switch__muted-line">Loading cards…</div>
      ) : cards.error ? (
        <div className="cs-plan-switch__error">{cards.error}</div>
      ) : (cards.data || []).length === 0 ? (
        <div className="cs-plan-switch__muted-line">
          No payment methods.{" "}
          <button
            type="button"
            className="cs-plan-switch__add-card"
            onClick={goAddCard}
          >
            Add a card first.
          </button>
        </div>
      ) : (
        <div className="cs-plan-switch__cards">
          {(cards.data || []).map((c) => (
            <div
              key={c.id}
              className={`cs-plan-switch__card${
                selectedCardId === c.id ? " cs-plan-switch__card--active" : ""
              }`}
              onClick={() => setSelectedCardId(c.id)}
            >
              <span>💳</span>
              <span>
                {brand(c.brand)} **** {c.last4}
              </span>
              {c.isDefault && (
                <span className="cs-plan-switch__card-default">(Default)</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="cs-plan-switch__total">Total due now: {totalLabel}</div>

      {error && <p className="cs-plan-switch__error">{error}</p>}
      {succeeded && (
        <p className="cs-plan-switch__success">
          ✓ Upgrade successful. Updating your plan…
        </p>
      )}

      <div className="cs-plan-switch__actions">
        <button
          type="button"
          className="cs-plan-switch__cancel"
          onClick={onClose}
        >
          {succeeded ? "Close" : "Cancel"}
        </button>
        {!succeeded && (
          <button
            type="button"
            className="cs-plan-switch__primary"
            disabled={submitting}
            onClick={handleUpgrade}
          >
            {submitting ? "Processing…" : `Pay ${totalLabel} & Upgrade`}
          </button>
        )}
      </div>
    </div>
  );
};

/** 升级套餐弹窗（流程三） */
const UpgradeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  currentPlan?: Plan;
  targetPlan: Plan | null;
  onSuccess: () => void;
}> = ({ open, onClose, currentPlan, targetPlan, onSuccess }) => {
  if (!targetPlan) return null;
  return (
    <Modal open={open} onClose={onClose}>
      <UpgradeForm
        currentPlan={currentPlan}
        targetPlan={targetPlan}
        onSuccess={onSuccess}
        onClose={onClose}
      />
    </Modal>
  );
};

export default UpgradeModal;
